import type { ReadonlyDeep } from 'type-fest';

import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import { HomepageTrendingSection } from './api.graphql';
import type { HomepageTrendingSectionQuery, HomepageTrendingSectionQueryVariables } from './api.graphql';
import { HomepageTrendingSectionDataError } from './types';

type TrendingPetition = NonNullable<HomepageTrendingSectionQuery['trendingPetitions']>['edges'][number];

export type TrendingSectionData = {
	trendingPetitions: ReadonlyDeep<TrendingPetition[]>;
	trendingTopics: HomepageTrendingSectionQuery['trendingTopics']['featuredTopics'];
};

type HomepageTrendingSectionDataParams = { locale: string; countryCode: string };

export async function getData(
	utilityContext: UtilityContext,
	{ countryCode, locale }: HomepageTrendingSectionDataParams,
): Promise<TrendingSectionData> {
	const {
		gql: { fetch },
		errorReporter,
	} = utilityContext;
	const { data } = await fetch<HomepageTrendingSectionQuery, HomepageTrendingSectionQueryVariables>({
		query: HomepageTrendingSection,
		variables: {
			locale,
			countryCode,
		},
		rejectOnError: false,
	});

	if (!data) {
		// No data returned
		const error = new HomepageTrendingSectionDataError('homepage_trending_section_data_not_found');
		void errorReporter.report(error);
		throw error;
	}

	if (data.trendingPetitions == null) {
		// No featured petition data
		void errorReporter.report(
			new HomepageTrendingSectionDataError('homepage_trending_section_data_not_found', {
				message: 'No featured petition data',
			}),
		);
	}

	const { success, status } = data.trendingTopics;
	if (!success) {
		// No featured topic data
		const errorMessage = status
			? `Unknown error calling getFeaturedTopicsByLocale query (status: ${status})`
			: 'Unknown error calling getFeaturedTopicsByLocale query';
		void errorReporter.report(
			new HomepageTrendingSectionDataError('homepage_trending_section_data_not_found', { message: errorMessage }),
		);
	}

	return {
		trendingPetitions: data.trendingPetitions?.edges ?? [],
		trendingTopics: data.trendingTopics.featuredTopics ?? [],
	};
}
